import { Spinner, Button } from "@tellescope/design-system";
import { cn } from "@tellescope/utils";
import { useExternalRedirect, useBlockNavigation, useFallbackFocus } from "@tellescope/react-hooks";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { EnduserSession } from "@tellescope/sdk";
import type { FormResponse, OrganizationTheme } from "@tellescope/types-models";
import {
  useTellescopeEnduserForm,
  isFillableEnduserForm,
  type UseTellescopeEnduserFormArgs,
} from "../hooks/use-tellescope-enduser-form";
import { useFormAnalytics } from "../hooks/use-form-analytics";
import { EnduserFormWizardField } from "../EnduserFormWizardField";
import { FieldTitleFontSizeWrapper } from "../FieldTitleFontSizeWrapper";
import { FieldFeedback } from "../FieldFeedback";
import type { ContinueToFormInApp } from "../useResolveRedirectTarget";
import { EnduserFormWizardThanksScreen } from "../EnduserFormWizardThanksScreen";
import { EnduserFormWizardSubmittingScreen } from "../EnduserFormWizardSubmittingScreen";
import { selectHasPendingFiles, type FormShallowStoreHook } from "../store";
import {
  resolvePostSubmitRedirect,
  calculateFormScoring,
  resolveFormCustomizationStyle,
  consumesEnterKey,
} from "../utils";
import { FormRealTimeScores } from "./FormRealTimeScores";
import { FormWizardLogo } from "./FormWizardLogo";
import { FormBlockingErrors } from "./FormBlockingErrors";

export interface EnduserFormWizardBodyProps {
  formArgs: Omit<UseTellescopeEnduserFormArgs, "useFormShallowStore">;
  useFormShallowStore: FormShallowStoreHook;
  onComplete: () => void;
  session?: EnduserSession | null;
  organizationSubdomain?: string;
  orgLogoSrc?: string;
  organization?: OrganizationTheme;
  documentGroupBackUrl?: string;
  continueToFormInApp?: ContinueToFormInApp;
  restartUrl?: string;
  renderDownloadAction?: (submittedResponse: FormResponse) => ReactNode;
}

export const EnduserFormWizardBody = ({
  formArgs,
  useFormShallowStore,
  onComplete,
  session,
  organizationSubdomain,
  orgLogoSrc,
  organization,
  documentGroupBackUrl,
  continueToFormInApp,
  restartUrl,
  renderDownloadAction,
}: EnduserFormWizardBodyProps) => {
  const { t } = useTranslation("forms");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const fieldContainerRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const enduserFormResult = useTellescopeEnduserForm({ ...formArgs, useFormShallowStore });
  const hasNoRootField = !!enduserFormResult && "hasNoRootField" in enduserFormResult;
  const enduserForm = isFillableEnduserForm(enduserFormResult) ? enduserFormResult : undefined;
  const { pendingFilesByFieldId, submittedResponse } = useFormShallowStore(
    "pendingFilesByFieldId",
    "submittedResponse"
  );
  const hasPendingFiles = selectHasPendingFiles(pendingFilesByFieldId);

  // A Redirect field navigates away on purpose, and a submit in flight is already being persisted —
  // neither should trip the warning. Legacy excluded Redirect the same way (forms.tsx:566-567).
  useBlockNavigation(
    hasPendingFiles && enduserForm?.activeField?.value.type !== "Redirect" && enduserForm?.submitPhase === "idle"
  );

  // Only the tag injection lives here; the form_progress/form_submitted events are reported by the
  // navigation and submit handlers that cause them.
  useFormAnalytics(enduserForm?.form?.gtmTag, enduserForm?.form?.ga4measurementId);

  // A field that renders nothing focusable — a `description`, or one that ignores `autoFocus` —
  // would otherwise leave the page with no focus at all, and Enter with nothing to act on. Focusing
  // Next gives the key a target and shows a keyboard user where they are.
  useFallbackFocus(fieldContainerRef, nextButtonRef, [enduserForm?.activeField?.value.id]);

  const customization = enduserForm?.form?.customization;
  const wrapperStyle = resolveFormCustomizationStyle({
    customization,
    backgroundColor: enduserForm?.form?.backgroundColor,
    themeColor: organization?.themeColor,
    themeColorSecondary: organization?.themeColorSecondary,
  });

  // Only honored when the form opts in; the link is captured by the booking field regardless.
  const bookedAppointmentUrl = enduserForm?.form?.redirectToBookedAppointmentOnSubmit
    ? enduserForm?.bookedAppointmentUrl
    : undefined;

  const postSubmitRedirectURL = resolvePostSubmitRedirect({
    submitRedirectURL: enduserForm?.form?.submitRedirectURL,
    backUrl: documentGroupBackUrl,
    serverRedirectUrl: enduserForm?.serverRedirectUrl,
    bookedAppointmentUrl,
  });

  const autoSubmitRedirectURL = enduserForm?.autoSubmitted ? postSubmitRedirectURL : undefined;
  const resolveAutoSubmitRedirect = useCallback(
    async () => (autoSubmitRedirectURL ? { url: autoSubmitRedirectURL } : undefined),
    [autoSubmitRedirectURL]
  );
  // Stays undefined until the autosubmit lands, so the hook's fire-once latch is not spent on a
  // render that has no destination yet.
  useExternalRedirect(autoSubmitRedirectURL ? resolveAutoSubmitRedirect : undefined);

  if (isSubmitted) {
    return (
      <div className="flex flex-col h-full" style={wrapperStyle}>
        <EnduserFormWizardThanksScreen
          htmlMessage={enduserForm?.form?.htmlThanksMessage}
          message={enduserForm?.form?.thanksMessage}
          onDone={onComplete}
          restartUrl={customization?.showRestartAtEnd ? restartUrl : undefined}
          downloadSlot={
            enduserForm?.form?.publicShowDownload && submittedResponse
              ? renderDownloadAction?.(submittedResponse)
              : undefined
          }
        />
      </div>
    );
  }

  if (hasNoRootField) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-full" style={wrapperStyle}>
        <p className="text-sm text-destructive">{t("Start question not found for form")}</p>
      </div>
    );
  }

  if (!enduserForm || !enduserForm.activeField) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-full" style={wrapperStyle}>
        <Spinner size="sm">{t("Loading form...")}</Spinner>
      </div>
    );
  }

  const {
    activeField,
    getFieldProps,
    currentResponses,
    interpolateFieldText,
    enduser,
    form,
    goToNextField,
    goToPreviousField,
    isNextDisabled,
    isPreviousDisabled,
    showSubmit,
    submitForm,
    isSubmitted: isResponseSubmitted,
    submitFormMutation,
    submitPhase,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    uploadProgress,
    progress,
    fieldsArr,
    logicOptions,
    applyDatabasePrepopulation,
    setCustomerId,
    setBookedAppointmentUrl,
    hasBlockingFieldError,
    blockingFieldErrors,
    goToField,
  } = enduserForm;

  // Owning the whole panel while submitting is what makes the double-submit guard in useSubmitPhase
  // unreachable through the UI — no form control is mounted to click.
  if (submitPhase !== "idle") {
    return (
      <div className="flex flex-col h-full" style={wrapperStyle}>
        <EnduserFormWizardSubmittingScreen submitPhase={submitPhase} uploadProgress={uploadProgress} />
      </div>
    );
  }

  // An autosubmitted response is complete, so the wizard shows the same thanks screen the
  // Submit button leads to rather than returning to the answered field.
  if (autoSubmitted) {
    // A configured redirect takes precedence over the thanks screen: showing it would let the
    // patient click Done and close the dialog, only for the pending navigation to yank them
    // elsewhere. Hold on a spinner until the redirect lands. Gated on the normalized URL so a
    // target that cannot be navigated to falls through rather than holding the spinner forever.
    if (postSubmitRedirectURL) {
      return (
        <div className="flex flex-col items-center justify-center p-8 h-full" style={wrapperStyle}>
          <Spinner size="sm">{t("Redirecting. Please wait...")}</Spinner>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full" style={wrapperStyle}>
        <EnduserFormWizardThanksScreen
          htmlMessage={form?.htmlThanksMessage}
          message={form?.thanksMessage}
          onDone={onComplete}
          restartUrl={customization?.showRestartAtEnd ? restartUrl : undefined}
          downloadSlot={
            form?.publicShowDownload && submittedResponse ? renderDownloadAction?.(submittedResponse) : undefined
          }
        />
      </div>
    );
  }

  const percentageComplete = progress.toFixed(0);

  // Scored off the same active-response set conditional logic uses, so a Score branch and the cards
  // on screen can never disagree — an abandoned branch's answers are excluded from both.
  const realTimeScores =
    form?.realTimeScoring && form.scoring?.length
      ? calculateFormScoring({ response: { responses: logicOptions.activeResponses ?? [] }, form })
      : undefined;

  // Enter advances, except where the active field needs the key itself: `stringLong` inserts a
  // newline, a Question Group may end in one, and a free-text Dropdown commits the typed value.
  const fieldType = activeField.value.type;
  const isFreeTextDropdown =
    fieldType === "Dropdown" && !!activeField.value.options?.other && !activeField.value.options?.radio;
  const advancesOnEnter =
    fieldType !== "stringLong" && fieldType !== "Question Group" && !isFreeTextDropdown && !showSubmit;

  // Scoped to the field container rather than the window, so Enter only advances when it was pressed
  // within the question itself. `consumesEnterKey` then yields the key to controls that act on it —
  // a dropdown (open or closed) and a search input would otherwise select *and* advance at once.
  // Going back to fix something ends this submit attempt: the enduser is acting on the list, so the
  // next time they reach the end they get a live Submit button rather than a pre-disabled one.
  const handleSelectBlockingField = (fieldId: string) => {
    setHasAttemptedSubmit(false);
    goToField(fieldId);
  };

  const handleFieldKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" || !advancesOnEnter || isNextDisabled) return;
    if (consumesEnterKey(e.target)) return;
    e.preventDefault();
    goToNextField();
  };

  return (
    <div className="flex flex-col h-full" style={wrapperStyle}>
      {!customization?.hideProgressBar && (
        <div className="px-4 py-2 border-b flex-shrink-0">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t("{{percentageComplete}}% complete", {
                percentageComplete,
              })}
            </span>
          </div>
          <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${percentageComplete}%` }}
            />
          </div>
        </div>
      )}
      {!customization?.hideLogo && (
        <FormWizardLogo
          formLogoURL={customization?.logoURL}
          orgLogoSrc={organization?.logoURL ?? orgLogoSrc}
          orgName={organization?.name}
          logoHeight={customization?.logoHeight}
          logoAlignment={customization?.logoAlignment}
        />
      )}
      {/* Keyed on the active field so each page mounts a fresh scroll container, landing at the top the way
          legacy's explicit scroll-to-top did on next and prev. */}
      <div key={activeField.value.id} className="flex-1 overflow-y-auto p-4">
        <div ref={fieldContainerRef} className="flex flex-col gap-4 min-w-full" onKeyDown={handleFieldKeyDown}>
          {!!session && (
            <FieldTitleFontSizeWrapper titleFontSize={activeField.value.titleFontSize}>
              <EnduserFormWizardField
                node={activeField}
                session={session}
                enduserId={formArgs.enduserId}
                getFieldProps={getFieldProps}
                form={form}
                currentResponses={currentResponses}
                interpolateFieldText={interpolateFieldText}
                enduser={enduser}
                fieldsArr={fieldsArr}
                logicOptions={logicOptions}
                organizationSubdomain={organizationSubdomain}
                submitForm={submitForm}
                documentGroupBackUrl={documentGroupBackUrl}
                isResponseSubmitted={isResponseSubmitted}
                formResponseId={formArgs.responseId}
                rootResponseId={formArgs.rootResponseId}
                continueToFormInApp={continueToFormInApp}
                applyDatabasePrepopulation={applyDatabasePrepopulation}
                setCustomerId={setCustomerId}
                setBookedAppointmentUrl={setBookedAppointmentUrl}
                goToPreviousField={goToPreviousField}
                isPreviousDisabled={isPreviousDisabled}
                autoFocus
              />
            </FieldTitleFontSizeWrapper>
          )}
          <FieldFeedback
            feedback={activeField.value.feedback}
            value={currentResponses[activeField.value.id]?.answer.value}
          />

          {realTimeScores && realTimeScores.length > 0 && <FormRealTimeScores scores={realTimeScores} />}
          {/* Deliberately a fixed string rather than the server's message, which is untranslated and written
              for an API caller, not a patient. Every failure here has the same remedy. */}
          {(submitFormMutation.error || submitError || autoSubmitError) && (
            <p className="text-sm text-destructive">{t("Failed to submit form. Please try again.")}</p>
          )}
        </div>
      </div>
      <div className="px-4 py-3 border-t flex-shrink-0">
        {/* Withheld until the enduser actually tries to submit: before that, an error on a page they
            have moved on from is not yet standing in their way, and listing it would be nagging. */}
        {showSubmit && hasAttemptedSubmit && (
          <div className="mb-3">
            <FormBlockingErrors blockingErrors={blockingFieldErrors} onSelectField={handleSelectBlockingField} />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPreviousField}
            disabled={isPreviousDisabled}
            className={cn(isPreviousDisabled && "invisible")}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("Back")}
          </Button>

          {showSubmit ? (
            <Button
              size="sm"
              onClick={async () => {
                setSubmitError(false);
                // Revealing the list is what this click does when the form cannot be submitted; the
                // button then disables itself until the enduser has resolved what it named.
                if (hasBlockingFieldError) {
                  setHasAttemptedSubmit(true);
                  return;
                }
                let result: Awaited<ReturnType<typeof submitForm>>;
                try {
                  result = await submitForm();
                } catch {
                  setSubmitError(true);
                  return;
                }
                // The server destination is read from this submit's own result rather than the rendered value: the
                // store write that records it lands a render later, which the navigation below does not wait for.
                const redirectURL = resolvePostSubmitRedirect({
                  submitRedirectURL: form?.submitRedirectURL,
                  backUrl: documentGroupBackUrl,
                  serverRedirectUrl: result.redirectTo || result.nextFormGroupPublicURL,
                  bookedAppointmentUrl,
                });
                if (redirectURL) {
                  window.location.href = redirectURL;
                  return;
                }
                setIsSubmitted(true);
              }}
              // Gated like Next until the enduser has tried once: the earlier fields' errors only
              // take the button away after the click that put them on screen.
              disabled={
                isNextDisabled ||
                submitFormMutation.isPending ||
                isAutoSubmitting ||
                (hasAttemptedSubmit && hasBlockingFieldError)
              }
            >
              {t("Submit")}
            </Button>
          ) : (
            <Button ref={nextButtonRef} size="sm" onClick={goToNextField} disabled={isNextDisabled || isAutoSubmitting}>
              {t("Next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
